import { useCallback, useContext, useMemo, useState } from "react";

import {
	defaultAddressBookItem,
	useQuoteAddressContext,
} from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { getAppliedAddressLabel } from "~/features/quote/utils/applied-address.ts";
import { buildStopPostalCode } from "~/features/quote/utils/build-stop-postal-code.ts";
import { resolveAddressBookCountry } from "~/features/quote/utils/resolve-address-book-country.ts";
import type { SearchResult } from "~/shared/components/fields/address-postal-code-search.tsx";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type {
	Country,
	ZipCode,
} from "~/shared/schemas/location-state-schema.ts";
import type {
	OnChange,
	QuoteStateType,
	SubGroupKey,
} from "~/shared/types/quote-details-types.ts";
import {
	ddUseFavAddress,
	toDdWorkflow,
} from "~/shared/utils/datadog-actions.ts";

import { blankAddress } from "../../../constants/quote-detail-constants.ts";

type UseAddressPostalCodeSelectionArgs = {
	name: SubGroupKey;
	countries: Country[];
	onChange: (field: keyof QuoteStateType, value: OnChange) => void;
	quoteState: QuoteStateType;
	applyAddressBookAccessorials: (address: AddressBookItemSummary) => void;
};

export function useAddressPostalCodeSelection({
	name,
	countries,
	onChange,
	quoteState,
	applyAddressBookAccessorials,
}: UseAddressPostalCodeSelectionArgs) {
	const { selectedAddressBookItems, setSelectedAddressBookItem } =
		useQuoteAddressContext();
	const { workflow } = useContext(QuoteDetailsContext);
	const [showAddressBookModal, setShowAddressBookModal] = useState(false);
	const [pendingAddressBookItem, setPendingAddressBookItem] =
		useState<AddressBookItemSummary | null>(null);
	const [invalidCountryAddress, setInvalidCountryAddress] =
		useState<AddressBookItemSummary | null>(null);

	const clearInvalidCountryAddress = useCallback(() => {
		setInvalidCountryAddress(null);
	}, []);

	const addressBookItem = useMemo(() => {
		return selectedAddressBookItems[name];
	}, [name, selectedAddressBookItems]);

	const postalCode = quoteState[
		`${name}PostalCode` as keyof typeof quoteState
	] as ZipCode;

	const stopType = name === "pickup" ? 1 : 2;

	const searchValue = useMemo(() => {
		const appliedLabel = getAppliedAddressLabel(addressBookItem);
		if (appliedLabel) {
			return appliedLabel;
		}
		if (postalCode?.postalCode) {
			return `${postalCode.postalCode} - ${postalCode.city.name}, ${postalCode.city.state.abbreviation}, ${postalCode.city.state.countryCode}`;
		}
		return "";
	}, [addressBookItem, postalCode]);

	const onToggleAddressBookModal = () => {
		setShowAddressBookModal(!showAddressBookModal);
	};

	const hasUserSelectedAccessorials = useMemo(() => {
		const currentServices = quoteState[`${name}Services`] ?? [];
		// User has non-default selections if they have services beyond just Commercial
		if (currentServices.length === 0) return false;
		if (currentServices.length > 1) return true;
		return currentServices[0]?.name !== "Commercial";
	}, [name, quoteState]);

	const applyAddressBookCountry = useCallback(
		(address: AddressBookItemSummary) => {
			const selectedCountry = resolveAddressBookCountry(address, countries);
			if (selectedCountry) {
				onChange(`${name}Country` as keyof QuoteStateType, selectedCountry);
			}
		},
		[countries, name, onChange],
	);

	// Applies a saved address's location (address book item + country + the
	// quoteState postal code) without touching accessorials. Centralizing the
	// postal-code sync here keeps `quoteState.{type}PostalCode` from drifting out
	// of sync with the applied address on every path that applies one (search,
	// "Keep Current" accessorials prompt, address book modal).
	const applyAddressBookLocation = useCallback(
		(address: AddressBookItemSummary): boolean => {
			const hasCountry =
				address.country != null && address.country.trim() !== "";
			if (
				hasCountry &&
				countries.length > 0 &&
				!resolveAddressBookCountry(address, countries)
			) {
				setInvalidCountryAddress(address);
				return false;
			}
			setSelectedAddressBookItem(name, address);
			applyAddressBookCountry(address);
			onChange(
				`${name}PostalCode` as keyof QuoteStateType,
				buildStopPostalCode(address),
			);
			return true;
		},
		[
			applyAddressBookCountry,
			countries,
			name,
			onChange,
			setSelectedAddressBookItem,
		],
	);

	const applyAddressBookSelection = useCallback(
		(address: AddressBookItemSummary) => {
			if (!applyAddressBookLocation(address)) return;
			applyAddressBookAccessorials(address);
		},
		[applyAddressBookAccessorials, applyAddressBookLocation],
	);

	const handleAddressBookWithPrompt = useCallback(
		(address: AddressBookItemSummary) => {
			if (hasUserSelectedAccessorials) {
				setPendingAddressBookItem(address);
			} else {
				applyAddressBookSelection(address);
			}
		},
		[hasUserSelectedAccessorials, applyAddressBookSelection],
	);

	const handleAddressPostalCodeChange = (selectedItem: SearchResult | null) => {
		if (selectedItem === null) {
			onChange(`${name}PostalCode` as keyof QuoteStateType, blankAddress);
			setSelectedAddressBookItem(name, defaultAddressBookItem);
			return;
		}
		if (selectedItem?.type == "postalCode") {
			onChange(`${name}PostalCode` as keyof QuoteStateType, selectedItem.data);
			onChange(
				`${name}Country` as keyof QuoteStateType,
				selectedItem.data.city.state.country,
			);
			setSelectedAddressBookItem(name, defaultAddressBookItem);
		}
		if (selectedItem?.type == "addressBook") {
			handleAddressBookWithPrompt(selectedItem.data);
			if (selectedItem.isFavorite) {
				const section = name === "pickup" ? "pickup" : "drop";
				ddUseFavAddress(toDdWorkflow(workflow), section);
			}
		}
	};

	return {
		addressBookItem,
		applyAddressBookCountry,
		applyAddressBookLocation,
		applyAddressBookSelection,
		clearInvalidCountryAddress,
		handleAddressBookWithPrompt,
		handleAddressPostalCodeChange,
		invalidCountryAddress,
		onToggleAddressBookModal,
		pendingAddressBookItem,
		searchValue,
		selectedAddressBookItems,
		setPendingAddressBookItem,
		setSelectedAddressBookItem,
		showAddressBookModal,
		stopType,
	};
}
